import React, { useState, useEffect } from "react";
import { Backdrop, Pagination } from "@mui/material";
import styles from "./ReportsDetailModal.module.scss";
import { SuccessEditClose } from "customIcons";
import CreditReportSummary from "../creditReportSummary/CreditReportSummary";
import ReportsDetailTable from "../reportsDetailTable/ReportsDetailTable";
import { useFetchCreditOrdersQuery } from "../creditReportsAPI";
import { ReportDetails } from "interfaces/common.interface";
import CreditReportSummarySkeleton from "@features/common/skeleton/creditReportSkeleton/CreditReportSummarySkeleton";
import ReportsDetailTableSkeleton from "@features/common/skeleton/creditReportSkeleton/ReportsDetailTableSkeleton";

// ============================================================================
// Types & Interfaces
// ============================================================================
interface ReportsDetailModalProps {
  onClose: () => void;
  report: ReportDetails;
}

const ReportsDetailModal: React.FC<ReportsDetailModalProps> = ({
  onClose,
  report,
}) => {
  // ============================================================================
  // Local State
  // ============================================================================
  const [currentPage, setCurrentPage] = useState(1);
  const [searchKey, setSearchKey] = useState("");
  const [debouncedSearchKey, setDebouncedSearchKey] = useState("");
  // ============================================================================
  // Effects
  // ============================================================================
  /**
   * Debounce searchKey to avoid excessive API calls.
   * Updates debouncedSearchKey 500ms after user stops typing.
   */
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchKey(searchKey);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchKey]);

  // ============================================================================
  // API Queries
  // ============================================================================
  const { data, isLoading } = useFetchCreditOrdersQuery({
    page: currentPage,
    spread_sheet_order_pk: report.id,
    search_key: debouncedSearchKey,
  });

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className="create-promotion-modal">
      <Backdrop open={true}>
        <div className={styles["modal-container"]}>
          {/* Modal Header */}
          <div className={styles["modal-container__header"]}>
            <h2>Credit Details</h2>
            <div onClick={onClose} style={{ cursor: "pointer" }}>
              <SuccessEditClose />
            </div>
          </div>

          {/* Credit Report Summary */}
          {isLoading ? (
            <CreditReportSummarySkeleton />
          ) : (
            <CreditReportSummary summaryData={report} />
          )}

          {/* Credit Orders Table */}
          {isLoading ? (
            <ReportsDetailTableSkeleton />
          ) : (
            <ReportsDetailTable
              data={data?.data}
              searchTerm={searchKey}
              onSearchChange={setSearchKey}
            />
          )}

          {/* Pagination Controls */}
          {
            <div className={styles["modal-container__pagination"]}>
              <Pagination
                count={data?.total_page || 1}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
                shape="rounded"
                className="compact-pagination"
              />
              <span>of {data?.total_page || 0} pages</span>
            </div>
          }
        </div>
      </Backdrop>
    </div>
  );
};

export default ReportsDetailModal;
